// ============================================================================
// JARVISTRAVEL - SERVER BUDGET INTELLIGENCE (JAR-2935)
// One hook behind every budget-intelligence surface: the projections, trend
// statuses, confidence and summary come from core (GET /budget/summary +
// /budget/forecast), not from the device (utils/budgetEngine.ts — Brent's
// server-is-the-source rule, JAR-2892). A local/demo trip id answers nothing —
// those surfaces keep the device engine there, the demo exception Brent rules
// on. A server failure answers nothing and is reported: the caller falls back
// to the device engine for that render rather than showing a fabricated
// answer, and the next revision re-asks.
//
// The cache is keyed per trip id + revision, so the three surfaces (dashboard
// alert rollup, BudgetForecast, BudgetManager) share one pair of requests
// instead of triple-fetching, and a budget write bumps the revision and
// re-asks.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { commitmentsFromWire, fetchBudgetSummary, fetchCategoryForecasts, forecastsFromWire, summaryFromWire, type CategoryCommitment } from '@/app/api/budgetForecast';
import { reportApiFailure } from '@/app/api/failure';
import { isBackendTripID } from '@/app/utils/tripIds';
import type { CategoryForecast, BudgetSummary } from '@/app/utils/budgetEngine';
import type { Trip } from '@/app/types';

export interface BudgetIntelligence {
  /** Core's trip summary, or undefined while loading / on a local trip / on failure. */
  summary: BudgetSummary | undefined;
  /** Core's per-category forecasts, or undefined on the same terms. */
  rows: CategoryForecast[] | undefined;
  /** What core holds for each category beyond its spending, by category code,
   *  or undefined on the same terms. */
  commitments: Record<string, CategoryCommitment> | undefined;
  /** True while the server request is in flight. Distinguishes the
   *  not-yet-answered render from the fell-back render (ocr-31/23). */
  loading: boolean;
  /** The server failure, when the fallback is showing because core could not
   *  answer. Surfaces may say so; a 401/403 is a broken integration, not a
   *  quiet reason to trust the device engine. */
  error: unknown;
  /** Re-ask the server now (after a budget write). */
  refresh: () => void;
}

type Answer = { summary?: BudgetSummary; rows?: CategoryForecast[]; commitments?: Record<string, CategoryCommitment>; error?: unknown } | null;

// Keyed tripId:revision:tick. Bounded per trip: a new revision or tick drops
// that trip's previous keys (the promise it held may still be in flight for
// the surface that asked for it, which is fine — the answer it resolves with
// stays valid for that render).
const cache = new Map<string, Promise<Answer>>();
const keysByTrip = new Map<string, string[]>();
let refreshTick = 0;
// One listener per mounted surface: (tripId, callback). A refresh re-ticks
// only the surfaces of the SAME trip — a budget write on trip A must not
// re-fetch trip B's answers on a dashboard showing several (ocr-15).
const refreshListeners = new Map<string, Set<() => void>>();

/**
 * Forget every cached answer. Called on sign-out (purgeAllLocalUserData): the
 * cache holds mapped budget amounts keyed by trip id with no user dimension,
 * and sign-out is an SPA navigation — the module realm — and its cache —
 * survives into the next person's session on a shared browser (the same shape
 * as lastKnownTrips, planIntent, explore favorites and preferences, each wired
 * here after a review). Not named __: it is production code, not a test hook.
 */
export function purgeBudgetIntelligenceCache(): void {
  cache.clear();
  keysByTrip.clear();
  // refreshTick and the listener sets are deliberately NOT cleared: listeners
  // belong to mounted components and die with them, and a bumped tick only
  // makes the next mount re-fetch — the safe direction (round-3 ocr-35).
}

/** Test hook — forget every entry and the listeners/tick too, so suites start independent. */
export function __resetBudgetIntelligenceCache(): void {
  purgeBudgetIntelligenceCache();
  refreshListeners.clear();
  refreshTick = 0;
}

// The trip's revision. updatedAt is written by core on every accepted write —
// itinerary batch, budget category, expense — so a fresh read after a budget
// write carries a new revision and re-asks. Absent only on a trip built before
// it was stamped; two such reads share one key, which the in-flight dedupe
// wants anyway.
const revisionOf = (trip: Trip): string => trip.updatedAt ?? '';

function load(trip: Trip, tick: number): Promise<Answer> | null {
  if (!isBackendTripID(trip.id)) return null;
  const key = `${trip.id}:${revisionOf(trip)}:${tick}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const promise = (async () => {
    try {
      const [wire, fwire] = await Promise.all([fetchBudgetSummary(trip.id), fetchCategoryForecasts(trip.id)]);
      return {
        summary: summaryFromWire(trip, wire),
        rows: forecastsFromWire(trip, fwire, wire.categories ?? []),
        commitments: commitmentsFromWire(wire.categories),
        error: undefined,
      };
    } catch (error) {
      // Not cached as an answer (a transient failure must retry on the next
      // revision or tick) but the failure IS the answer for this render:
      // surfaces can distinguish fell-back-because-down from loading.
      reportApiFailure('[budgetForecast] server intelligence failed; surfaces fall back to the device engine:', error);
      cache.delete(key);
      keysByTrip.set(trip.id, (keysByTrip.get(trip.id) ?? []).filter((k) => k !== key));
      return { summary: undefined, rows: undefined, error };
    }
  })();
  const previous = keysByTrip.get(trip.id) ?? [];
  for (const old of previous) cache.delete(old);
  keysByTrip.set(trip.id, [key]);
  // Bounded across trips too: a session that opens many backend trips must not
  // accumulate one entry each for the life of the page (ocr-17). Keep the last
  // 25; older trips re-fetch on their next visit, which is what a cold cache
  // already does.
  if (keysByTrip.size > 25) {
    const oldest = keysByTrip.keys().next().value as string;
    for (const old of keysByTrip.get(oldest) ?? []) cache.delete(old);
    keysByTrip.delete(oldest);
  }
  cache.set(key, promise);
  return promise;
}

export function useBudgetIntelligence(trip: Trip | null | undefined): BudgetIntelligence {
  const [tick, setTick] = useState(refreshTick);
  const [answer, setAnswer] = useState<Answer>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const backend = trip ? isBackendTripID(trip.id) : false;
  const tripId = trip?.id ?? '';

  // A refresh() from ANY surface of THIS trip bumps the shared tick for the
  // trip's surfaces: a budget write in the sheet re-answers the banner and the
  // bell too, without re-fetching other trips' answers (ocr-15). (ocr-16: the
  // tick used to be per-caller, so the other surfaces kept their stale answer.)
  useEffect(() => {
    if (!tripId) return;
    const onRefresh = () => setTick(refreshTick);
    let set = refreshListeners.get(tripId);
    if (!set) refreshListeners.set(tripId, (set = new Set()));
    set.add(onRefresh);
    return () => {
      set?.delete(onRefresh);
      if (set && set.size === 0) refreshListeners.delete(tripId);
    };
  }, [tripId]);

  useEffect(() => {
    if (!backend || !trip) {
      setAnswer(null);
      setLoading(false);
      setError(undefined);
      return;
    }
    let cancelled = false;
    const promise = load(trip, tick);
    if (!promise) {
      setAnswer(null);
      setLoading(false);
      setError(undefined);
      return;
    }
    setLoading(true);
    promise.then((result) => {
      if (!cancelled) {
        setAnswer(result);
        setError(result?.error);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
    // `trip` is read inside through the closure; re-fires on id, revision and refresh tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend, tripId, trip ? revisionOf(trip) : '', tick]);

  const refresh = useCallback(() => {
    // No trip, nothing to re-ask — and the shared tick must not move for a
    // no-op (round-3 ocr-37), or a later-mounted surface inherits a higher
    // tick than its key was built with.
    if (!tripId) return;
    refreshTick += 1;
    // This surface's own listener is in the trip's set, so one fan-out re-ticks
    // the caller too (ocr-33: no second state update).
    for (const l of refreshListeners.get(tripId) ?? []) l();
  }, [tripId]);

  return {
    summary: answer?.summary,
    rows: answer?.rows,
    commitments: answer?.commitments,
    loading,
    error,
    refresh,
  };
}
